// @flow
import find from 'lodash/find';
import get from 'lodash/get';
import StringUtils from './utils/string';
import { interpolateWith } from './utils/format';
import type {
  VisionBenefitResultType,
  VisionBenefitType,
} from '../types/vision-plan';
import type { HealthBenefitType } from '../types/benefit';
import { PAY_TYPES } from './constants/benefit';

const templates: { [key: string]: Function } = {
  copay: ({ amt, suffix }) => `$${amt}${suffix || ''}`,
  copayRange: ({ amountMin, amountMax }) => `$${amountMin}-${amountMax}`,
  coins: ({ amt, isVerbose }) => `${StringUtils.getPercentageDisplayLabel(amt)}${isVerbose ? ' of price' : ''}`,
  coinsRange: ({ amountMin, amountMax }) => `${amountMax}-${amountMin}%`,
  coinsAsSavings: ({ amountMax }) => `${100 - amountMax}%`,
  coinsAsSavingsRange: ({ amountMin, amountMax }) => `${100 - amountMin}-${100 - amountMax}%`,
};

export function getVisionList({
  benefits,
  asSavings = true,
}: {
  benefits: Array<VisionBenefitType>,
  asSavings?: boolean,
}): Array<VisionBenefitResultType> {
  return benefits.reduce((results, benefit) => {
    const hasAmountMin = typeof benefit.amountMin === 'number';
    const result: VisionBenefitResultType = {
      ...benefit,
      amountSavings: null,
      value: '',
    };

    switch (benefit.payType) {
      case 'copay': {
        return [...results, {
          ...result,
          value: hasAmountMin ?
            templates.copayRange(benefit) :
            templates.copay({ amt: benefit.amountMax }),
        }];
      }
      case 'allowance': {
        // When there are both allowance and coins for the same serviceType,
        // it’s assumed that coins apply to amount over allowance
        const coinsMatch = find(benefits, { serviceType: benefit.serviceType, payType: 'coins' });

        return [...results, {
          ...result,
          value: templates.copay({ amt: benefit.amountMax }),
          amountSavings: coinsMatch ? 100 - coinsMatch.amountMax : null,
        }];
      }
      case 'coins': {
        // Let the allowance case handle adding the coinsAmount
        if (find(benefits, { serviceType: benefit.serviceType, payType: 'allowance' })) {
          return results;
        }

        const methodBaseName = asSavings ? 'coinsAsSavings' : 'coins';
        const value = hasAmountMin ?
          templates[`${methodBaseName}Range`](benefit) :
          templates[methodBaseName]({ ...benefit, amt: benefit.amountMax });

        return [...results, {
          ...result,
          value,
        }];
      }
      default: return results;
    }
  }, []);
}

export function getBasicText({
  benefit,
  isPostDeductible = true,
  commonTranslations,
}: {
  benefit: HealthBenefitType,
  isPostDeductible?: boolean,
  commonTranslations?: Object,
}): string {
  let using;
  if (!benefit) {
    return get(commonTranslations, 'price.notApplicable', 'N/A');
  }

  // Post deductible or No deductible
  if (benefit.deductible === 'none' || benefit.deductible === 'false' || isPostDeductible) {
    if (benefit.amt) {
      switch (benefit.payType) {
        case PAY_TYPES.COINS:
          using = {
            amountPercentage: `${benefit.amt}%`,
          };
          if (benefit.isVerbose) {
            return interpolateWith(
              get(commonTranslations, `disclaimers.${PAY_TYPES.COINS}Verbose`),
              using,
            );
          }
          return interpolateWith(
            get(commonTranslations, `disclaimers.${PAY_TYPES.COINS}`),
            using,
          );
        case PAY_TYPES.DUAL:
          using = {
            amountPercentage: `${benefit.amtDual}%`,
            amount: `${benefit.amt}`,
          };
          return interpolateWith(
            get(commonTranslations, `disclaimers.${PAY_TYPES.DUAL}`),
            using,
          );
        case PAY_TYPES.COPAY:
          using = {
            amount: benefit.amt,
            suffix: benefit.suffix || '',
          };
          return interpolateWith(
            get(commonTranslations, `disclaimers.${PAY_TYPES.COPAY}`),
            using,
          );
        default:
          break;
      }
    }
    return get(commonTranslations, 'price.noCost', 'No cost');
  }

  // Pre deductible
  if (!benefit.preQty) {
    return get(commonTranslations, 'price.fullPrice', 'Full price');
  }
  return benefit.preAmt === 0
    ? get(commonTranslations, 'price.free', 'FREE')
    : `$${benefit.preAmt}`;
}

export function getFormattedText({
  benefit,
  isPostDeductible = false,
  isVerbose = false,
  suffix = '',
  omitAmt = false,
  commonTranslations,
}: {
  benefit: HealthBenefitType,
  isPostDeductible: boolean,
  isVerbose: boolean,
  suffix: string,
  omitAmt: boolean,
  commonTranslations: Object,
}): string {
  let using;
  if (!benefit) {
    return get(commonTranslations, 'price.notApplicable', 'N/A');
  }

  if (benefit.deductible === 'none' || benefit.deductible === 'false' || isPostDeductible) {
    return getBasicText({ benefit, suffix, isVerbose, isPostDeductible, commonTranslations });
  }

  if (!benefit.preQty) {
    return get(commonTranslations, 'price.fullPrice');
  }

  if (omitAmt) {
    using = { x: benefit.preQty };
    return benefit.preQty === 1
      ? get(commonTranslations, 'disclaimers.firstVistThen', 'First visit, then you pay full price')
      : interpolateWith(
        get(commonTranslations, 'disclaimers.firstXVisitsThen', `First ${benefit.preQty} visits, then you pay full price`),
        using,
      );
  }

  const preAmt = benefit.preAmt === 0
    ? get(commonTranslations, 'price.free') : `$${benefit.preAmt}`;

  if (benefit.preQty === 1) {
    using = { x: preAmt };
    return isVerbose ? interpolateWith(get(commonTranslations, 'disclaimers.xForFirstVisitVerbose'), using)
      : interpolateWith(get(commonTranslations, 'disclaimers.xForFirstVisit'), using);
  }

  using = { x: preAmt, y: benefit.preQty };
  return isVerbose ? interpolateWith(get(commonTranslations, 'disclaimers.xForFirstYVerbose'), using)
    : interpolateWith(get(commonTranslations, 'disclaimers.xForFirstY'), using);
}
